import {
  StudyFile,
  StudyFileUpload,
  StudyFilesFilters,
  StudyFileVersionsResponse,
} from '@/types/studyFiles';
import {
  getStudyFiles,
  uploadStudyFile,
  deleteStudyFile,
  getStudyFileVersions,
} from './studyFilesApi';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const STUDY_FILES_QUERY_KEY = 'studyFiles';
const STUDY_FILE_VERSIONS_QUERY_KEY = 'studyFileVersions';

/**
 * Hook to fetch study files
 */
export const useStudyFiles = (filters?: StudyFilesFilters) => {
  return useQuery({
    queryKey: [STUDY_FILES_QUERY_KEY, filters],
    queryFn: () => getStudyFiles(filters),
    select: data => ({
      files: data.data,
      pagination: data.pagination,
    }),
  });
};

/**
 * Hook to upload a new study file
 */
export const useUploadStudyFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uploadData: StudyFileUpload) => uploadStudyFile(uploadData),
    onSuccess: () => {
      // Invalidate and refetch study files immediately
      queryClient.invalidateQueries({ queryKey: [STUDY_FILES_QUERY_KEY] });
      // Also invalidate any cached file versions
      queryClient.invalidateQueries({
        queryKey: [STUDY_FILE_VERSIONS_QUERY_KEY],
      });
    },
  });
};

/**
 * Hook to delete a study file
 */
export const useDeleteStudyFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileId: number) => deleteStudyFile(fileId),
    onSuccess: () => {
      // Invalidate and refetch study files
      queryClient.invalidateQueries({ queryKey: [STUDY_FILES_QUERY_KEY] });
    },
  });
};

/**
 * Hook to upload multiple study files
 */
export const useUploadMultipleStudyFiles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (uploads: StudyFileUpload[]): Promise<StudyFile[]> => {
      const uploadPromises = uploads.map(uploadData =>
        uploadStudyFile(uploadData)
      );
      return Promise.all(uploadPromises);
    },
    onSuccess: () => {
      // Invalidate and refetch study files
      queryClient.invalidateQueries({ queryKey: [STUDY_FILES_QUERY_KEY] });
    },
  });
};

/**
 * Hook to fetch study file versions
 */
export const useStudyFileVersions = (studyFileId: number, enabled = false) => {
  return useQuery({
    queryKey: [STUDY_FILE_VERSIONS_QUERY_KEY, studyFileId],
    queryFn: () => getStudyFileVersions(studyFileId),
    enabled,
    select: (data: StudyFileVersionsResponse) => data.data,
  });
};
